import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  resource,
} from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { ToastController } from '@ionic/angular';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { ButtonComponent } from '../ui/button/button.component';
import { EnvService } from '../../services/env.service';
import { QrCodeService } from '../../services/qr-code.service';

/** Rendered size of the QR PNG in pixels (download and clipboard copy use the same image). */
const QR_SIZE_PX = 400;

/** Decodes a base64 data URL into a Blob for the clipboard. */
export function pngBlobFromDataUrl(dataUrl: string): Blob {
  const [header, base64 = ''] = dataUrl.split(',');
  const type = /^data:([^;]+)/.exec(header)?.[1] ?? 'image/png';
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
  return new Blob([bytes], { type });
}

@Component({
  selector: 'app-trip-link-qr',
  imports: [FaIconComponent, TranslocoDirective, ButtonComponent],
  templateUrl: './trip-link-qr.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TripLinkQrComponent {
  private readonly envService = inject(EnvService);
  private readonly qrCode = inject(QrCodeService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);

  tripId = input.required<number>();
  /** What the copy button puts on the clipboard: the public URL or the QR image. */
  copy = input<'link' | 'image'>('link');

  readonly publicUrl = computed(() => {
    const base = this.envService.mainSiteUrl.replace(/\/$/, '');
    return `${base}/${this.tripId()}`;
  });

  /** QR PNG generated on the device as a data URL — no third-party service, no blob URL to revoke. */
  private readonly qrImage = resource({
    params: () => this.publicUrl(),
    loader: ({ params }) => this.qrCode.toPngDataUrl(params, QR_SIZE_PX),
  });

  readonly qrSrc = computed(() => (this.qrImage.hasValue() ? this.qrImage.value() : null));

  async copyLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.publicUrl());
      const toast = await this.toastController.create({
        message: this.transloco.translate(marker('trip-link.copied')),
        duration: 2000,
        position: 'bottom',
        color: 'success',
      });
      await toast.present();
    } catch {
      const toast = await this.toastController.create({
        message: this.transloco.translate(marker('trip-link.copy')),
        duration: 2000,
        position: 'bottom',
      });
      await toast.present();
    }
  }

  async copyQrImage(): Promise<void> {
    const src = this.qrSrc();
    if (!src) {
      return;
    }
    try {
      const blob = pngBlobFromDataUrl(src);
      await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
      const toast = await this.toastController.create({
        message: this.transloco.translate(marker('trip-link.copied-qr')),
        duration: 2000,
        position: 'bottom',
        color: 'success',
      });
      await toast.present();
    } catch {
      const toast = await this.toastController.create({
        message: this.transloco.translate(marker('trip-link.copy-qr-error')),
        duration: 3000,
        position: 'bottom',
        color: 'danger',
      });
      await toast.present();
    }
  }

  downloadQr(): void {
    const src = this.qrSrc();
    if (!src) {
      return;
    }
    const link = document.createElement('a');
    link.href = src;
    link.download = `trip-${this.tripId()}-qr.png`;
    link.click();
  }
}
